import React, { useState, useEffect } from "react";
import { Sidebar } from "../../components";
import { Icon } from "../../datos";
import { useAuth } from "../../hooks";
import { Formulario } from "../../api";
import { Link, useLocation } from "react-router-dom";

export function AdminLayout(props) {
  const { children } = props;
  const { user, accessToken, logout } = useAuth();
  const [nuevosCVs, setNuevosCVs] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const location = useLocation();

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false);
    setShowNotifications(false);
  }, [location.pathname]);

  useEffect(() => {
    const fetchNuevos = async () => {
      if (!accessToken) return;
      try {
        const formularioController = new Formulario();
        const response = await formularioController.getGestionHumana(accessToken, "nuevo", undefined, 1);
        if (response && response.total !== undefined) {
          setNuevosCVs(response.total);
        }
      } catch (error) {
        console.error("Error fetching notifications:", error);
        if (error.message && (error.message.includes('Token') || error.message.includes('expirado') || error.message.includes('inválido'))) {
          logout();
        }
      }
    };

    fetchNuevos();
    const intervalId = setInterval(fetchNuevos, 30000);
    return () => clearInterval(intervalId);
  }, [accessToken, logout, location.pathname]);

  return (
    <div className="flex min-h-screen bg-[#F3F4F8]">
      {/* Mobile overlay backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar — fixed drawer on mobile, static on desktop */}
      <div className={`
        fixed lg:static inset-y-0 left-0 z-50 lg:z-auto
        flex-shrink-0 bg-[#151C84] shadow-[4px_0_24px_rgba(21,28,132,0.25)]
        flex flex-col
        transform transition-all duration-300 ease-in-out
        ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
        ${sidebarCollapsed ? "lg:w-[72px]" : "lg:w-[260px]"}
        w-[260px]
      `}>
        {/* Logo */}
        <div className={`border-b border-white/10 flex items-center flex-shrink-0 ${sidebarCollapsed ? "px-3 py-5 justify-center" : "px-5 py-5 justify-between"}`}>
          {!sidebarCollapsed && (
            <div className="flex items-center gap-3">
              <Icon.LogoWhite className="h-10 w-auto" />
            </div>
          )}
          {sidebarCollapsed && (
            <div className="w-9 h-9 rounded-xl bg-[#C81416] flex items-center justify-center flex-shrink-0">
              <span className="text-white font-extrabold text-sm">C</span>
            </div>
          )}
          {/* Close button — mobile only */}
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-1.5 text-white/50 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Nav */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden">
          <Sidebar collapsed={sidebarCollapsed} />
        </div>

        {/* Collapse toggle — desktop only */}
        <div className="hidden lg:flex items-center px-3 py-3 border-t border-white/10">
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className={`flex items-center gap-2 w-full py-2 px-2 rounded-xl text-white/70 hover:text-white hover:bg-white/10 transition-all ${sidebarCollapsed ? "justify-center" : ""}`}
            title={sidebarCollapsed ? "Expandir menú" : "Colapsar menú"}
          >
            <svg
              className={`w-5 h-5 flex-shrink-0 transition-transform duration-300 ${sidebarCollapsed ? "rotate-180" : ""}`}
              fill="none" stroke="currentColor" viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
            </svg>
            {!sidebarCollapsed && <span className="text-xs font-medium">Colapsar</span>}
          </button>
        </div>
      </div>

      {/* Main */}
      <div className="flex-1 flex flex-col min-h-screen overflow-hidden">
        {/* Topbar */}
        <div className="h-[60px] lg:h-[70px] bg-white border-b border-gray-100 flex items-center justify-between px-4 lg:px-8 shadow-sm flex-shrink-0 z-30 relative">
          <div className="flex items-center gap-3">
            {/* Hamburger — mobile only */}
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 text-gray-500 hover:text-[#151C84] hover:bg-[#151C84]/10 rounded-xl transition-all"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <div className="w-1 h-7 bg-[#C81416] rounded-full hidden lg:block" />
            <div>
              <h1 className="text-sm lg:text-base font-bold text-gray-900 leading-tight">Panel Administrativo</h1>
              <p className="text-[10px] lg:text-xs text-gray-400 hidden sm:block">Inversiones Catelli</p>
            </div>
          </div>

          <div className="flex items-center gap-2 lg:gap-3">
            {/* Notifications */}
            <div className="relative">
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative w-9 h-9 lg:w-10 lg:h-10 flex items-center justify-center text-gray-500 hover:text-[#151C84] hover:bg-blue-50 rounded-xl transition-all duration-200"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                {nuevosCVs > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-[#C81416] text-white text-[9px] font-bold flex items-center justify-center rounded-full shadow-[0_2px_6px_rgba(200,20,22,0.5)]">
                    {nuevosCVs > 9 ? "9+" : nuevosCVs}
                  </span>
                )}
              </button>

              {showNotifications && (
                <div className="absolute right-0 mt-2 w-72 lg:w-80 bg-white rounded-2xl shadow-[0_8px_32px_rgba(21,28,132,0.16)] border border-gray-100 overflow-hidden z-50 animate-fade-in">
                  <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-[#151C84]/4">
                    <h3 className="font-bold text-gray-900 text-sm">Notificaciones</h3>
                    <span className="text-[10px] bg-[#C81416] text-white px-2 py-0.5 rounded-full font-bold">
                      {nuevosCVs} Nuevas
                    </span>
                  </div>
                  <div className="max-h-72 overflow-y-auto">
                    {nuevosCVs > 0 ? (
                      <Link
                        to="/admin/gestionhumana"
                        onClick={() => setShowNotifications(false)}
                        className="flex items-start gap-3 p-4 hover:bg-gray-50 transition-colors"
                      >
                        <div className="w-10 h-10 rounded-xl bg-[#151C84]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <svg className="w-5 h-5 text-[#151C84]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </div>
                        <div>
                          <p className="text-sm font-bold text-gray-900">Nuevos CVs recibidos</p>
                          <p className="text-xs text-gray-500 mt-0.5">{nuevosCVs} prospecto(s) esperando revisión.</p>
                          <p className="text-xs text-[#C81416] font-bold mt-2">Ver candidatos →</p>
                        </div>
                      </Link>
                    ) : (
                      <div className="p-8 text-center">
                        <svg className="w-10 h-10 mx-auto text-gray-200 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                        </svg>
                        <p className="text-sm text-gray-400 font-medium">Sin notificaciones nuevas</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Divider */}
            <div className="w-px h-6 bg-gray-200 hidden sm:block" />

            {/* User */}
            <Link to="/admin/perfil" className="flex items-center gap-2 lg:gap-3 group rounded-xl px-2 py-1 hover:bg-gray-50 transition-colors">
              <div className="w-8 h-8 lg:w-9 lg:h-9 bg-[#151C84] rounded-xl flex items-center justify-center text-white font-bold text-sm shadow-[0_4px_12px_rgba(21,28,132,0.3)] group-hover:shadow-[0_4px_16px_rgba(21,28,132,0.4)] transition-shadow">
                {user?.name?.charAt(0)?.toUpperCase() || "A"}
              </div>
              <div className="hidden md:block">
                <p className="font-bold text-gray-900 text-sm leading-tight group-hover:text-[#151C84] transition-colors">{user?.name || "Admin"}</p>
                <p className="text-[11px] text-gray-400 font-medium">Mi perfil</p>
              </div>
            </Link>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-4 lg:p-8 overflow-auto">
          {children}
        </div>
      </div>
    </div>
  );
}
